import { clock } from '@tabel/shared/agent-events/texts';
import type { ThreadView } from '@tabel/shared/schemas/agent-client';
import { Archive, ArrowLeft, MessagesSquare, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { ErrorCodes, errorCode, errorSentence } from '../../components/ErrorToast';
import { LABEL, PLACEHOLDER, STATUS_LABEL, T, TITLE, agentName, say } from '../../components/assistant/texts';
import { Button, EmptyState, Skeleton, inputCls } from '../../components/ui';
import { ApiError } from '../../lib/errors';
import { localToday, ruDate, ruDateTime } from '../../lib/format';

/*
 * «Мои разговоры» на странице «Помощник» (T115; contracts/ui.md §1; US7; мокап Assistant.dc.html `.threads`):
 * - только свои разговоры, новые сверху; строка — название и «Claude · работает» / «Claude · сегодня 13:40»;
 * - поиск по названию — сразу при вводе;
 * - внизу «Архив · N разговоров» — список архива; в архиве — «К разговорам»;
 * - пусто, загрузка, ошибка с кодом и «Повторить».
 * Время — по времени объекта.
 */

/** Когда был последний ответ: «сегодня 13:40», «вчера 17:12», «16.10.2026»; работает — состояние. */
export function whenText(t: ThreadView, tz: string, now = Date.now()): string {
  if (t.status === 'running' || t.status === 'waiting' || t.status === 'stopping')
    return STATUS_LABEL[t.status];
  const at = t.lastTurnAt ?? t.createdAt;
  const day = (iso: string) => {
    const d = ruDateTime(iso, tz);
    return `${d.slice(6, 10)}-${d.slice(3, 5)}-${d.slice(0, 2)}`;
  };
  const today = localToday(tz, now);
  const yesterday = localToday(tz, now - 86_400_000);
  const d = day(at);
  if (d === today) return say.today(clock(at, tz));
  if (d === yesterday) return say.yesterday(clock(at, tz));
  return ruDate(d);
}

export function ThreadList({
  threads,
  archivedCount,
  archiveView,
  selectedId,
  query,
  onQuery,
  onSelect,
  onToggleArchive,
  loading,
  error,
  onRetry,
  tz,
}: {
  threads: readonly ThreadView[] | undefined;
  archivedCount: number;
  archiveView: boolean;
  selectedId: string | null;
  query: string;
  onQuery: (q: string) => void;
  onSelect: (id: string) => void;
  onToggleArchive: () => void;
  loading: boolean;
  error: unknown;
  onRetry: () => void;
  tz: string;
}) {
  const q = query.trim().toLowerCase();
  const shown = (threads ?? []).filter((t) => !q || t.title.toLowerCase().includes(q));
  let body: ReactNode;
  if (error && !threads) {
    const requestId = error instanceof ApiError ? error.requestId : null;
    const code = errorCode(error);
    body = (
      <div role="alert" className="flex flex-col items-start gap-2 px-3 py-4 text-[13px]">
        <p className="flex items-center gap-2 font-semibold text-ink">
          <TriangleAlert size={16} strokeWidth={1.75} className="text-bad" aria-hidden />
          {TITLE.listError}
        </p>
        <p className="text-ink-2">{errorSentence(error)}</p>
        {(requestId || code) && (
          <p className="text-[12px] text-ink-2">
            {LABEL.errorCode} <ErrorCodes requestId={requestId} code={code} />
          </p>
        )}
        <Button size="sm" onClick={onRetry}>
          {LABEL.retry}
        </Button>
      </div>
    );
  } else if (loading && !threads) {
    body = (
      <div aria-busy="true" aria-label={LABEL.loading} className="space-y-3 px-3 py-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-4 w-4/5" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        ))}
      </div>
    );
  } else if ((threads ?? []).length === 0) {
    body = (
      <EmptyState
        icon={archiveView ? Archive : MessagesSquare}
        title={archiveView ? TITLE.noArchive : TITLE.noThreads}
        description={archiveView ? T.noArchive : T.noThreads}
      />
    );
  } else if (shown.length === 0) {
    body = <p className="px-3 py-4 text-[13px] text-ink-2">{T.noMatch}</p>;
  } else {
    body = (
      <ul className="flex flex-col gap-0.5">
        {shown.map((t) => {
          const on = t.id === selectedId;
          return (
            <li key={t.id}>
              <button
                type="button"
                aria-current={on ? 'true' : undefined}
                onClick={() => onSelect(t.id)}
                className={`flex min-h-10 w-full flex-col gap-0.5 rounded-control px-2.5 py-2 text-left transition-colors ${on ? 'bg-accent-soft' : 'hover:bg-subtle'}`}
              >
                <span className="w-full truncate text-[14px] leading-[1.4] text-ink">{t.title}</span>
                <span className="text-[12px] text-ink-2">
                  {say.threadLine(agentName(t.agent), whenText(t, tz))}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <nav aria-label={archiveView ? TITLE.archive : TITLE.myThreads} className="flex min-h-0 flex-1 flex-col">
      <div className="px-3 pt-3 pb-2">
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          aria-label={LABEL.search}
          placeholder={PLACEHOLDER.search}
          className={inputCls}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">{body}</div>
      <div className="border-t border-line p-2">
        <Button
          size="sm"
          variant="ghost"
          icon={archiveView ? ArrowLeft : Archive}
          onClick={onToggleArchive}
          className="w-full justify-start! max-tablet:min-h-10"
        >
          {archiveView
            ? LABEL.backFromArchive
            : archivedCount > 0
              ? say.archiveCount(archivedCount)
              : TITLE.archive}
        </Button>
      </div>
    </nav>
  );
}
